import type { ApiEnvelope } from "../shared/api-types";

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly code = "REQUEST_FAILED",
    readonly status = 0,
    readonly details?: Record<string, string>,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  csrfToken?: string | null;
  idempotencyKey?: string;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body !== undefined) headers.set("Content-Type", "application/json");
  if (options.csrfToken) headers.set("X-CSRF-Token", options.csrfToken);
  if (options.idempotencyKey) headers.set("X-Idempotency-Key", options.idempotencyKey);

  let response: Response;
  try {
    response = await fetch(path, {
      ...options,
      headers,
      credentials: "same-origin",
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
    });
  } catch {
    throw new ApiRequestError("网络连接失败，请检查连接后重试。", "NETWORK_ERROR");
  }

  let envelope: ApiEnvelope<T>;
  try {
    envelope = (await response.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiRequestError("服务器返回了无法识别的响应。", "INVALID_RESPONSE", response.status);
  }

  if (!response.ok || !envelope.success || envelope.data === null) {
    throw new ApiRequestError(
      envelope.error?.message ?? "请求失败，请稍后重试。",
      envelope.error?.code ?? "REQUEST_FAILED",
      response.status,
      envelope.error?.details,
    );
  }
  return envelope.data;
}

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "发生未知错误。";
}
